import React, { useEffect, useState } from "react";
import Modal from "../components/Modal";
import PageLoader from "./PageLoader";
import { adminApiRequest } from "../utils/adminApi";

export default function EasterBookings({ setIsAnyModalActive }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openSlot, setOpenSlot] = useState(null);
  const [selectedBookingId, setSelectedBookingId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchBookings = async (showLoader = true) => {
    if (showLoader) setLoading(true);

    try {
      const data = await adminApiRequest({
        script: "get-easter-bookings",
      });
      
      if (data?.success || data?.status === "success") {
        const rawSlots = data.slots || [];
        const slotsArray = Array.isArray(rawSlots) ? rawSlots : Object.values(rawSlots);
        setSlots(slotsArray);
      }
    } catch (err) {
      console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings(true);
    const interval = setInterval(() => fetchBookings(false), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleDelete = async () => {
    if (!selectedBookingId) return;
    setDeleting(true);

    try {
      const data = await adminApiRequest({
        script: "delete-easter-booking",
        data: { booking_id: selectedBookingId },
      });

      if (data?.status === "success") {
        setSelectedBookingId(null);
        fetchBookings(false);
      } else {
        alert(data.message || "Failed to delete booking");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("Error connecting to server");
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return <PageLoader message="Fetching Event Data..." />;
  }

  return (
    <div className="flex h-full flex-col p-6 overflow-hidden">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Easter Buffet Bookings</h1>
          <p className="text-sm text-slate-500">
            Monitor time slots and guest lists for the Easter event
          </p>
        </div>
        <button title="Refresh"
          onClick={() => fetchBookings(true)}
          className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-800"
        >
          <i className="fa-solid fa-rotate mr-2"></i> Refresh
        </button>
      </div>

      <div className="flex-1 overflow-auto sidebar-scrollbar pr-1">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
          {slots.map((slot) => {
            const isFull = slot.booked_count >= slot.max_capacity;
            const isExpanded = openSlot === slot.time_slot;

            return (
              <div
                key={slot.time_slot}
                className={`flex flex-col rounded-2xl border transition shadow-sm bg-white ${
                  isFull ? "border-red-100 bg-red-50/10" : "border-slate-200"
                }`}
              >
                <div className="p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-slate-900">
                      {slot.time_slot}
                    </h3>
                    <span
                      className={`rounded-lg px-2.5 py-1 text-xs font-bold ${
                        isFull
                          ? "bg-red-100 text-red-700"
                          : "bg-emerald-100 text-emerald-700"
                      }`}
                    >
                      {slot.booked_count} / {slot.max_capacity}
                    </span>
                  </div>

                  <div className="relative h-2 w-full overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full transition-all duration-500 ${
                        isFull ? "bg-red-500" : "bg-emerald-500"
                      }`}
                      style={{
                        width: `${Math.min(
                          (slot.booked_count / slot.max_capacity) * 100,
                          100,
                        )}%`,
                      }}
                    ></div>
                  </div>

                  <button
                    onClick={() => setOpenSlot(isExpanded ? null : slot.time_slot)}
                    className="mt-5 w-full rounded-xl border border-slate-200 py-2.5 text-xs font-bold text-slate-600 transition hover:bg-slate-50"
                  >
                    {isExpanded ? "Hide Guest List" : "View Guest List"}
                  </button>
                </div>

                {isExpanded && (
                  <div className="border-t border-slate-100 bg-slate-50/50 p-5">
                    {slot.bookings.length > 0 ? (
                      <div className="space-y-3">
                        {slot.bookings.map((booking) => (
                          <div
                            key={booking.id}
                            className="flex items-center justify-between rounded-xl border border-white bg-white p-3 shadow-sm ring-1 ring-slate-100 transition hover:ring-indigo-200"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm font-bold text-slate-900">
                                {booking.customer_name}
                              </p>
                              <p className="text-xs text-slate-500 tabular-nums">
                                {booking.customer_phone}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-xs font-black text-slate-900">
                                x{booking.number_of_people}
                              </span>
                              <button title="Delete"
                                onClick={() => setSelectedBookingId(booking.id)}
                                className="flex h-7 w-7 items-center justify-center rounded-lg bg-red-100 text-red-600 transition hover:bg-red-600 hover:text-white"
                              >
                                <i className="fa-solid fa-trash-can text-[10px]"></i>
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="py-4 text-center text-xs text-slate-400">
                        No bookings for this slot yet.
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selectedBookingId && (
        <Modal
          title="Delete Booking?"
          onClose={() => setSelectedBookingId(null)}
          onSubmit={handleDelete}
          submitButtonText={deleting ? "Deleting..." : "Delete"}
          disableSubmit={deleting}
          showCancel={!deleting}
          showSubmitButton={true}
          submitButtonClass="bg-red-600 hover:bg-red-700 text-white"
        >
          <p className="text-sm text-slate-600">
            Are you sure you want to remove this booking? This action is permanent and will free up the slot capacity.
          </p>
        </Modal>
      )}
    </div>
  );
}
